import { GAMES, NONE_GAME_ID } from "../core/games.js";
import { computeMaxFitResolution, resolveEffectiveResolution } from "../core/scaling.js";

function setGenerateDisabled(refs, disabled) {
    refs.generateButton.disabled = disabled;
}

export function renderGameOptions(refs, selectedGameId) {
    refs.gameSelect.innerHTML = "";

    GAMES.forEach((game) => {
        const option = document.createElement("option");
        option.value = game.id;
        option.textContent = game.label;

        if (game.id === selectedGameId) {
            option.selected = true;
        }

        refs.gameSelect.append(option);
    });
}

export function renderGameMeta(refs, game) {
    refs.gameMetaTitle.textContent = game?.recommendationTitle ?? "No Recommendations";
    refs.gameMetaResolution.textContent = game?.tileResolutionRecommendation ?? "Select a target game to view recommendations.";
    refs.gameMetaUsage.textContent = game?.usageRecommendation ?? "Game-specific usage guidance will appear here.";
    refs.gameMeta?.classList.toggle("is-sticky", game?.id && game.id !== NONE_GAME_ID);
}

function formatByteSize(byteSize) {
    if (!Number.isFinite(byteSize) || byteSize < 0) {
        return "Unknown";
    }

    if (byteSize < 1024) {
        return `${byteSize} B`;
    }

    const units = ["KB", "MB", "GB"];
    let value = byteSize / 1024;
    let unitIndex = 0;

    while (value >= 1024 && unitIndex < units.length - 1) {
        value /= 1024;
        unitIndex += 1;
    }

    return `${value.toFixed(1)} ${units[unitIndex]}`;
}

export function renderStatus(refs, state) {
    refs.statusText.textContent = state.statusMessage;
    refs.progressBar.value = state.progressPercent;
    refs.cancelButton.disabled = state.workflow !== "generating";

    if (state.workflow === "generating") {
        refs.progressBar.hidden = false;
        setGenerateDisabled(refs, true);
    } else {
        refs.progressBar.hidden = true;
        setGenerateDisabled(refs, state.workflow === "empty");
    }
}

export function renderCropOverlay(refs, crop, imageMeta) {
    if (!crop || !imageMeta) {
        refs.cropOverlay.style.display = "none";
        return;
    }

    refs.cropOverlay.style.display = "block";
    refs.cropOverlay.style.left = `${(crop.x / imageMeta.widthPx) * 100}%`;
    refs.cropOverlay.style.top = `${(crop.y / imageMeta.heightPx) * 100}%`;
    refs.cropOverlay.style.width = `${(crop.width / imageMeta.widthPx) * 100}%`;
    refs.cropOverlay.style.height = `${(crop.height / imageMeta.heightPx) * 100}%`;
}

export function renderPreviewImage(refs, bitmap) {
    const context = refs.previewCanvas.getContext("2d", { alpha: false });
    context.clearRect(0, 0, refs.previewCanvas.width, refs.previewCanvas.height);

    if (!bitmap) {
        return;
    }

    context.drawImage(bitmap, 0, 0, refs.previewCanvas.width, refs.previewCanvas.height);
}

function formatUpscaleMeta(config, crop, imageMeta) {
    if (!config || !imageMeta) {
        return {
            text: "Unknown",
            emphasize: false
        };
    }

    const sourceWidth = crop?.width ?? imageMeta.widthPx;
    const sourceHeight = crop?.height ?? imageMeta.heightPx;
    const maxFitResolution = computeMaxFitResolution(sourceWidth, sourceHeight, config.tilesWide, config.tilesHigh);
    const selectedResolution = Number(config.tileResolution);
    const effectiveResolution = resolveEffectiveResolution(config.tileResolution, maxFitResolution, config.scalingMode);
    const exceedsMaxFit = Number.isFinite(maxFitResolution) && maxFitResolution > 0 && selectedResolution > maxFitResolution;
    const requiredWidth = config.tilesWide * effectiveResolution;
    const requiredHeight = config.tilesHigh * effectiveResolution;
    const widthScale = requiredWidth / sourceWidth;
    const heightScale = requiredHeight / sourceHeight;
    const upscaleFactor = Math.max(widthScale, heightScale);

    if (upscaleFactor > 1) {
        return {
            text: `Yes (${upscaleFactor.toFixed(2)}x)`,
            emphasize: exceedsMaxFit
        };
    }

    return {
        text: "No",
        emphasize: exceedsMaxFit
    };
}

function formatResolution(value) {
    return Number.isFinite(value) && value > 0 ? `${value} px` : "? px";
}

export function renderScalingMeta(refs, imageMeta, config, crop) {
    if (!refs.maxFitResolutionValue || !refs.effectiveResolutionValue || !refs.effectiveResolutionHelper) {
        return;
    }

    const sourceWidth = crop?.width ?? imageMeta?.widthPx;
    const sourceHeight = crop?.height ?? imageMeta?.heightPx;
    const maxFitResolution = computeMaxFitResolution(sourceWidth, sourceHeight, config?.tilesWide, config?.tilesHigh);
    const selectedResolution = Number(config?.tileResolution);
    const effectiveResolution = resolveEffectiveResolution(selectedResolution, maxFitResolution, config?.scalingMode);
    const exceedsMaxFit = Number.isFinite(maxFitResolution) && maxFitResolution > 0 && selectedResolution > maxFitResolution;
    refs.maxFitResolutionValue.textContent = formatResolution(maxFitResolution);
    refs.effectiveResolutionValue.textContent = formatResolution(effectiveResolution);
    refs.effectiveResolutionValue.classList.toggle("resolution-emphasis", exceedsMaxFit);
}

function formatAspectRatio(width, height) {
    if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
        return "unknown";
    }

    const safeWidth = Math.round(width);
    const safeHeight = Math.round(height);
    if (!Number.isInteger(safeWidth) || !Number.isInteger(safeHeight)) {
        return `${(width / height).toFixed(2)}:1`;
    }

    let a = Math.abs(safeWidth);
    let b = Math.abs(safeHeight);

    while (b !== 0) {
        const remainder = a % b;
        a = b;
        b = remainder;
    }

    const gcd = a || 1;
    return `${safeWidth / gcd}:${safeHeight / gcd}`;
}

export function renderImageMeta(refs, imageMeta, config, crop) {
    if (!imageMeta) {
        refs.imageMeta.textContent = "No image loaded.";
        return;
    }

    const upscaleMeta = formatUpscaleMeta(config, crop, imageMeta);
    const aspectRatio = formatAspectRatio(imageMeta.widthPx, imageMeta.heightPx);

    refs.imageMeta.textContent = `Image: ${imageMeta.widthPx} x ${imageMeta.heightPx} px (${aspectRatio}) | Size: ${formatByteSize(imageMeta.byteSize)} | Upscale: `;

    const upscaleValue = document.createElement("span");
    if (upscaleMeta.emphasize) {
        upscaleValue.className = "resolution-emphasis";
    }
    upscaleValue.textContent = upscaleMeta.text;
    refs.imageMeta.append(upscaleValue);
}
